import React from 'react';
import { Settings, Key, Eye, EyeOff, X, Save, AlertCircle } from 'lucide-react';

const ApiKeysModal = ({
    isOpen,
    onClose,
    apiKeys,
    setApiKeys,
    showKeyValues,
    setShowKeyValues
}) => {
    console.log("ApiKeysModal rendered with isOpen:", isOpen);
    if (!isOpen) return null;

    const handleKeyChange = (keyType, value) => {
        const newKeys = { ...apiKeys, [keyType]: value };
        setApiKeys(newKeys);
        localStorage.setItem('launchpad-api-keys', JSON.stringify(newKeys));
    };

    const toggleKeyVisibility = (keyType) => {
        setShowKeyValues(prev => ({
            ...prev,
            [keyType]: !prev[keyType]
        }));
    };

    const validateKey = (keyType, key) => {
        if (!key) return null;

        const patterns = {
            openai: /^sk-[A-Za-z0-9_-]+$/,
            anthropic: /^sk-ant-[A-Za-z0-9_-]+$/,
            templated: /^[A-Za-z0-9-]+$/
        };

        return patterns[keyType]?.test(key);
    };

    const clearAllKeys = () => {
        const emptyKeys = { openai: '', anthropic: '', templated: '' };
        setApiKeys(emptyKeys);
        localStorage.setItem('launchpad-api-keys', JSON.stringify(emptyKeys));
    };

    const keyDescriptions = {
        openai: "Used for AI content generation and DALL-E image creation",
        anthropic: "Used for Claude AI content generation",
        templated: "Used for Templated.io image generation service"
    };

    const keyLabels = {
        openai: "OpenAI API Key",
        anthropic: "Anthropic (Claude) API Key",
        templated: "Templated.io API Key"
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <Settings className="w-6 h-6 text-blue-600" />
                            <h2 className="text-2xl font-bold text-gray-900">API Configuration</h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Info Banner */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-blue-900 mb-1">Bring Your Own API Keys</h3>
                                <p className="text-blue-800 text-sm">
                                    Your API keys are stored locally in your browser and never sent to our servers.
                                    This ensures your usage and costs are under your control.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* API Key Fields */}
                    <div className="space-y-6">
                        {Object.keys(keyLabels).map((keyType) => (
                            <div key={keyType} className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    {keyLabels[keyType]}
                                </label>
                                <p className="text-xs text-gray-500 mb-2">
                                    {keyDescriptions[keyType]}
                                </p>
                                <div className="relative">
                                    <input
                                        type={showKeyValues[keyType] ? "text" : "password"}
                                        value={apiKeys[keyType]}
                                        onChange={(e) => handleKeyChange(keyType, e.target.value)}
                                        placeholder={`Enter your ${keyLabels[keyType].toLowerCase()}`}
                                        className={`w-full px-3 py-2 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${apiKeys[keyType] && validateKey(keyType, apiKeys[keyType]) === false
                                            ? 'border-red-300 bg-red-50'
                                            : apiKeys[keyType] && validateKey(keyType, apiKeys[keyType]) === true
                                                ? 'border-green-300 bg-green-50'
                                                : 'border-gray-300'
                                            }`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => toggleKeyVisibility(keyType)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showKeyValues[keyType] ? (
                                            <EyeOff className="w-4 h-4" />
                                        ) : (
                                            <Eye className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                                {apiKeys[keyType] && validateKey(keyType, apiKeys[keyType]) === false && (
                                    <p className="text-xs text-red-600">
                                        Invalid key format. Please check your {keyLabels[keyType].toLowerCase()}.
                                    </p>
                                )}
                                {apiKeys[keyType] && validateKey(keyType, apiKeys[keyType]) === true && (
                                    <p className="text-xs text-green-600">
                                        ✓ Key format looks valid
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Getting Keys Instructions */}
                    <div className="mt-8 space-y-4">
                        <h3 className="font-semibold text-gray-900">How to get your API keys:</h3>
                        <div className="space-y-3 text-sm text-gray-600">
                            <div className="flex items-start gap-2">
                                <span className="font-medium text-blue-600">OpenAI:</span>
                                <span>Visit <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">platform.openai.com/api-keys</a></span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="font-medium text-purple-600">Anthropic:</span>
                                <span>Visit <a href="https://console.anthropic.com/account/keys" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">console.anthropic.com/account/keys</a></span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="font-medium text-green-600">Templated.io:</span>
                                <span>Visit <a href="https://templated.io/dashboard" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">templated.io/dashboard</a></span>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                        <button
                            onClick={clearAllKeys}
                            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                            Clear All Keys
                        </button>
                        <div className="flex gap-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                            >
                                <Save className="w-4 h-4" />
                                Save & Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApiKeysModal;
